import { apiFetch } from '@/lib/api';

export interface KnowledgeArticle {
  id: string;
  title: string;
  content: string;
  category: string | null;
  status: 'draft' | 'published';
  created_by: string | null;
  created_at: string;
  updated_at: string;
  chunkCount?: number;
}

export const knowledgeService = {
  async listArticles(filters?: {
    category?: string;
    status?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: KnowledgeArticle[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.category) params.set('category', filters.category);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
    const res = await apiFetch(`/api/admin/knowledge/articles?${params}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to list articles');
    return json;
  },

  async getArticle(id: string): Promise<KnowledgeArticle> {
    const res = await apiFetch(`/api/admin/knowledge/articles/${id}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to get article');
    return json.data;
  },

  async createArticle(data: {
    title: string;
    content: string;
    category?: string;
    status?: 'draft' | 'published';
  }): Promise<KnowledgeArticle> {
    const res = await apiFetch('/api/admin/knowledge/articles', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create article');
    return json.data;
  },

  async updateArticle(id: string, data: {
    title?: string;
    content?: string;
    category?: string;
    status?: 'draft' | 'published';
  }): Promise<KnowledgeArticle> {
    const res = await apiFetch(`/api/admin/knowledge/articles/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to update article');
    return json.data;
  },

  async deleteArticle(id: string): Promise<void> {
    const res = await apiFetch(`/api/admin/knowledge/articles/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const json = await res.json();
      throw new Error(json.error || 'Failed to delete article');
    }
  },

  async vectorize(id: string): Promise<{ chunkCount: number }> {
    const res = await apiFetch(`/api/admin/knowledge/articles/${id}/vectorize`, { method: 'POST' });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to vectorize');
    return json.data;
  },

  async createFromTicket(ticketId: string): Promise<KnowledgeArticle> {
    const res = await apiFetch(`/api/admin/knowledge/from-ticket/${ticketId}`, { method: 'POST' });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to create from ticket');
    return json.data;
  },

  async getCategories(): Promise<string[]> {
    const res = await apiFetch('/api/admin/knowledge/categories');
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to get categories');
    return json.data;
  },
};
